import { Box, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@mui/material';
import ToggleTabs, { ToggleTabsInstance } from '@/components/toggle-tabs';
import Button from '../../button';
import useModal from '../../../hooks/use-modal.hook';
import Modal from '..';
import InlineToast from '@/components/toasts/inline';
import Loader from '@/components/loader';
import ListCard from '@/components/list-card';
import Text from '@/components/text';
import useFormHandle from '@/hooks/use-form-handle.hook';
import { OrgPolicyEngine, PolicyEngineMergeStrategyOptions } from '@/lib/models/org.model';
import { patchOrgPolicyEngine } from '@/lib/services/org.service';
import Card from '@/components/card';
import CardHeader from '@/components/card/card-header';
import Chip, { ChipColor } from '@/components/chip';
import NoneChip from '@/components/chip/none-chip';
import {
  ConversionImpact,
  ConversionIssue,
  ConversionIssueSeverity,
  ConversionIssueType,
  ConvertedPolicy,
  convertPoliciesToV2,
  getConversionIssueTypes,
  PolicyConversionResult,
  PolicyConversionRoute,
} from '@/lib/services/policy.service';
import Icon from '@/components/icon';
import Tooltip from '@/components/tooltip';
import { QueryKey } from '@/lib/query-client';
import { useQuery } from 'react-query';

export interface Props {
  orgId: string;
  orgName: string;
  // A dry run only shows the report. It cannot convert or switch.
  dryRunOnly?: boolean;
  onSwitched?: (policyEngine: OrgPolicyEngine) => void;
}

const useStyles = createUseStyles((theme: Theme) => ({
  routeComparison: {
    padding: 10,
    borderLeft: `4px solid ${theme.palette.gray}`,
  },
}));

const OrgWideGroup = 'Organization';

const Severities: ConversionIssueSeverity[] = ['blocking', 'warning', 'info'];

const SeverityLabels: Record<ConversionIssueSeverity, string> = {
  blocking: 'Blocking',
  warning: 'Warnings',
  info: 'Info',
};

const SeverityCountLabels: Record<ConversionIssueSeverity, [string, string]> = {
  blocking: ['blocking', 'blocking'],
  warning: ['warning', 'warnings'],
  info: ['info', 'info'],
};

const SeverityColors: Record<ConversionIssueSeverity, ChipColor> = {
  blocking: 'error',
  warning: 'warning',
  info: 'info',
};

type IssueTypes = Map<string, ConversionIssueType>;

const usersLabel = (count: number) => `${count} user${count === 1 ? '' : 's'}`;

const IssueImpact: FC<{ issue: ConversionIssue; impact?: ConversionImpact }> = ({
  issue,
  impact,
}) => {
  const { affectedUserCount, recentUserCount } = issue;

  if (affectedUserCount === undefined) {
    return null;
  }

  return (
    <Text size="small" color="grey">
      Affects {usersLabel(affectedUserCount)}
      {recentUserCount !== undefined &&
        `. ${recentUserCount} of them used these services in the last ${impact?.usage?.windowDays} days`}
      .
    </Text>
  );
};

const OverridePriority = 0;
const DefaultPriority = 10000;

const priorityLabel = (priority?: number) => {
  if (priority === OverridePriority) {
    return 'Override Policy';
  }
  if (priority === DefaultPriority) {
    return 'Default Policy';
  }
  return priority === undefined ? '' : `Priority ${priority}`;
};

// One v1 policy with the v2 policies it turns into and its issues. The section with no id holds the organization-wide issues.
// A section for a new policy with no v1 source has `added` set.
type PolicySection = {
  id: string;
  name: string;
  policies: ConvertedPolicy[];
  issues: ConversionIssue[];
  added?: ConvertedPolicy;
};

const buildSections = (issues: ConversionIssue[], policies: ConvertedPolicy[]): PolicySection[] => {
  const sections: Map<string, PolicySection> = new Map();
  const sectionFor = (id: string, name: string) => {
    if (!sections.has(id)) {
      sections.set(id, { id, name, policies: [], issues: [] });
    }
    return sections.get(id)!;
  };

  if (issues.some(({ policyId }) => !policyId)) {
    sectionFor('', OrgWideGroup);
  }

  policies.forEach((policy, index) => {
    const { sourcePolicyId, sourcePolicyName, name } = policy;
    if (!sourcePolicyId) {
      sectionFor(`added-${index}`, name).added = policy;
      return;
    }
    sectionFor(sourcePolicyId, sourcePolicyName || name).policies.push(policy);
  });

  issues.forEach((issue) => {
    const { policyId = '', policyName } = issue;
    sectionFor(policyId, policyName || policyId).issues.push(issue);
  });

  return [...sections.values()];
};

const IssueHelp: FC<{ code: string; issueTypes: IssueTypes }> = ({ code, issueTypes }) => {
  const issueType = issueTypes.get(code);

  if (!issueType) {
    return null;
  }

  return (
    <Tooltip
      title={
        <Box py={0.5}>
          <Text bold size="small" color="white">
            {issueType.title}
          </Text>
          <Text size="small" color="white">
            {issueType.description}
          </Text>
        </Box>
      }
    >
      <Icon name="info-circle" size="small" color="grey" />
    </Tooltip>
  );
};

const PolicySectionCard: FC<{
  section: PolicySection;
  issueTypes: IssueTypes;
  impact?: ConversionImpact;
}> = ({ section, issueTypes, impact }) => {
  const { id, name, policies, issues, added } = section;
  const appliesToAll = !id || added?.role === 'override';
  const userCount = appliesToAll ? impact?.userCount : impact?.policyUserCounts[id];
  const bySeverity = Severities.map((severity) => ({
    severity,
    issues: issues.filter((issue) => issue.severity === severity),
  })).filter(({ issues }) => issues.length);

  return (
    <Card>
      <CardHeader title={name} icon={id ? 'policy' : 'organization'}>
        <Stack direction="row" gap={0.5}>
          {added && (
            <Chip label={priorityLabel(added.priority) || 'New'} color="info" size="small" />
          )}
          {userCount !== undefined && <Chip label={usersLabel(userCount)} size="small" />}
          {bySeverity.map(({ severity, issues }) => (
            <Chip
              key={severity}
              label={`${issues.length} ${SeverityCountLabels[severity][issues.length === 1 ? 0 : 1]}`}
              color={SeverityColors[severity]}
              size="small"
            />
          ))}
        </Stack>
      </CardHeader>

      <Stack gap={2}>
        {added?.role === 'override' && (
          <Box>
            A new, empty policy for all users. It has no settings, so it changes no result. Use it
            for settings that must apply before every other policy.
          </Box>
        )}

        {!!id && !added && (
          <Box>
            <Text bold>Converts To</Text>
            {!policies.length && <NoneChip />}
            <Stack gap={0.5} mt={0.5}>
              {policies.map(({ name: policyName, priority }, index) => (
                <Stack key={`${policyName}-${index}`} direction="row" gap={1} alignItems="center">
                  <Icon name="arrow-right" size="small" color="grey" />
                  {policyName}
                  {priority !== undefined && (
                    <Chip label={priorityLabel(priority)} size="x-small" />
                  )}
                </Stack>
              ))}
            </Stack>
          </Box>
        )}

        {bySeverity.map(({ severity, issues }) => (
          <Box key={severity}>
            <Text bold>{SeverityLabels[severity]}</Text>
            <Stack gap={0.5} mt={0.5}>
              {issues.map((issue, index) => (
                <Stack
                  key={`${issue.code}-${index}`}
                  direction="row"
                  gap={1}
                  alignItems="flex-start"
                >
                  <Box flexGrow={1}>
                    {issue.message}
                    <IssueImpact issue={issue} impact={impact} />
                  </Box>
                  <IssueHelp code={issue.code} issueTypes={issueTypes} />
                </Stack>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Card>
  );
};

const WhatIsChecked: FC<{ issueTypes: IssueTypes }> = ({ issueTypes }) => {
  const groups = Severities.map((severity) => ({
    id: severity,
    name: SeverityLabels[severity],
    types: [...issueTypes.values()].filter((issueType) => issueType.severity === severity),
  })).filter(({ types }) => types.length);
  const typesById = new Map(groups.map(({ id, types }) => [id, types]));

  return (
    <ListCard
      title="What Is Checked"
      icon="doc-search"
      items={groups}
      onRenderItem={({ id, name }) => (
        <Box>
          <Text bold>{name}</Text>
          <Stack gap={1} mt={0.5}>
            {typesById.get(id as ConversionIssueSeverity)!.map(({ code, title, description }) => (
              <Box key={code}>
                <Text bold inline>
                  {title}:
                </Text>{' '}
                {description}
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    />
  );
};

const ConversionReport: FC<{ result: PolicyConversionResult; issueTypes: IssueTypes }> = ({
  result,
  issueTypes,
}) => {
  const [showChecks, setShowChecks] = useState(false);
  const { report, policies } = result;

  if (!report) {
    return null;
  }

  const sections = buildSections(report.issues, policies);

  return (
    <Stack gap={2}>
      <Stack direction="row" gap={1} alignItems="flex-start">
        <Box flexGrow={1}>
          {report.v1PolicyCount} v1 policies convert to {report.v2PolicyCount} v2 policies. The v1
          policies do not change.
          {report.existingV2PolicyCount > 0 &&
            ` The ${report.existingV2PolicyCount} v2 policies that the organization has now will be deleted.`}
          {report.impact &&
            report.impact.usage?.available === false &&
            ' Recent request data is not available for this organization, so the counts use group membership only.'}
        </Box>
        {!!issueTypes.size && (
          <Tooltip title={showChecks ? 'Hide what is checked' : 'What is checked'}>
            <Icon
              name="question-mark"
              size="small"
              color={showChecks ? 'active' : 'grey'}
              onClick={() => setShowChecks(!showChecks)}
            />
          </Tooltip>
        )}
      </Stack>

      {showChecks && <WhatIsChecked issueTypes={issueTypes} />}

      {sections.map((section) => (
        <PolicySectionCard
          key={section.id}
          section={section}
          issueTypes={issueTypes}
          impact={report.impact}
        />
      ))}
    </Stack>
  );
};

const RouteLabels: Record<PolicyConversionRoute, string> = {
  matched: 'Matched',
  'one-to-one': '1-to-1',
};

const RouteDescriptions: Record<PolicyConversionRoute, string> = {
  matched:
    'Some policies become more than one policy, so that each user keeps the same result as today.',
  'one-to-one':
    'Each policy becomes one policy with the same name. Some users can get a different result, as the warnings show.',
};

const Routes: PolicyConversionRoute[] = ['matched', 'one-to-one'];

type RouteResults = Record<PolicyConversionRoute, PolicyConversionResult>;

const warningsOf = (result: PolicyConversionResult) =>
  (result.report?.issues || []).filter(({ severity }) => severity === 'warning');

// 1-to-1 is simpler to manage, so it is recommended when its warnings affect no user.
const recommendedRoute = ({ 'one-to-one': oneToOne }: RouteResults): PolicyConversionRoute => {
  const report = oneToOne.report;
  const affectsNobody = warningsOf(oneToOne).every(
    ({ affectedUserCount }) => affectedUserCount === 0
  );
  return report && !report.blocking && !!report.impact && affectsNobody ? 'one-to-one' : 'matched';
};

const RouteComparison: FC<{ results: RouteResults; recommended: PolicyConversionRoute }> = ({
  results,
  recommended,
}) => {
  const styles = useStyles();

  return (
    <Card>
      <CardHeader title="Choose a Conversion" icon="tune" />
      <Stack className={styles.routeComparison} gap={2} mb={2}>
        {Routes.map((route) => {
          const { report, error } = results[route];
          const warnings = warningsOf(results[route]);
          const counted = warnings.filter(
            ({ affectedUserCount }) => affectedUserCount !== undefined
          );
          const mostAffected = Math.max(
            0,
            ...counted.map(({ affectedUserCount }) => affectedUserCount!)
          );

          return (
            <Box key={route}>
              <Stack direction="row" gap={1} alignItems="center">
                <Text bold>{RouteLabels[route]}</Text>
                {route === recommended && <Chip label="Recommended" color="success" size="small" />}
              </Stack>
              <Box>{RouteDescriptions[route]}</Box>
              <Text size="small" color="grey">
                {report
                  ? [
                      `${report.v2PolicyCount} new policies`,
                      `${warnings.length} warning${warnings.length === 1 ? '' : 's'}`,
                      !!counted.length && `the largest warning affects ${usersLabel(mostAffected)}`,
                      report.blocking && 'has a blocking issue',
                    ]
                      .filter(Boolean)
                      .join(', ')
                  : error}
              </Text>
            </Box>
          );
        })}
      </Stack>
    </Card>
  );
};

export const PolicyConversionModal: FC<Props> = ({
  orgId,
  orgName,
  dryRunOnly = false,
  onSwitched,
}) => {
  const { closeModal } = useModal();
  const tabRef = useRef<ToggleTabsInstance>(null);
  const { data: issueTypeList } = useQuery(
    [QueryKey.ConversionIssueTypes],
    getConversionIssueTypes,
    {
      staleTime: Infinity,
    }
  );
  const issueTypes = useMemo(
    () => new Map((issueTypeList || []).map((issueType) => [issueType.code, issueType])),
    [issueTypeList]
  );
  const [results, setResults] = useState<RouteResults | null>(null);
  const [route, setRoute] = useState<PolicyConversionRoute>('matched');
  const [error, setError] = useState('');

  const selected = results?.[route];
  const requiredEngineSettings = selected?.report?.requiredEngineSettings;

  // Convert again on each switch, so the org never runs stale v2 policies from an earlier switch.
  const formHandle = useFormHandle({
    initialValues: {},
    onSubmit: async () => {
      if (!requiredEngineSettings) {
        return;
      }

      setError('');

      const converted = await convertPoliciesToV2(orgId, 'apply', route);

      if (!converted.ok) {
        setError(converted.error || 'The policies were not converted');
        // A blocked real run returns the report that blocked it.
        if (converted.report) {
          setResults((old) => old && { ...old, [route]: converted });
        }
        return;
      }

      const policyEngine: OrgPolicyEngine = {
        version: 'v2',
        mergeStrategy: requiredEngineSettings.mergeStrategy,
      };
      const { patched, error: patchError } = await patchOrgPolicyEngine(orgId, policyEngine);

      if (!patched) {
        setError(
          `The policies were converted, but the organization still uses v1: ${patchError || 'the policy engine was not changed'}`
        );
        return;
      }

      onSwitched?.(policyEngine);
      closeModal();
    },
  });

  const { handleSubmit, isSubmitting } = formHandle;

  useEffect(() => {
    Promise.all(Routes.map((route) => convertPoliciesToV2(orgId, 'dry-run', route))).then(
      ([matched, oneToOne]) => setResults({ matched, 'one-to-one': oneToOne })
    );
  }, [orgId]);

  const recommended = results ? recommendedRoute(results) : 'matched';

  useEffect(() => {
    if (results) {
      tabRef.current?.setTabPath(recommendedRoute(results));
    }
  }, [results]);

  const mergeStrategyLabel = PolicyEngineMergeStrategyOptions.find(
    ({ value }) => value === requiredEngineSettings?.mergeStrategy
  )?.label;
  const canSwitch = !!selected?.report && !selected.report.blocking && !isSubmitting;

  const panels = useMemo(
    () =>
      Routes.map((panelRoute) => ({
        label: `${RouteLabels[panelRoute]}${panelRoute === recommended ? ' (Recommended)' : ''}`,
        path: panelRoute,
        component: results && (
          <Stack gap={2} pt={2}>
            {results[panelRoute].error && (
              <InlineToast level="error" message={results[panelRoute].error} />
            )}
            {results[panelRoute].report?.blocking && (
              <InlineToast
                level="error"
                message="The report has a blocking issue. Correct the current policies, then try again."
              />
            )}
            <ConversionReport result={results[panelRoute]} issueTypes={issueTypes} />
          </Stack>
        ),
      })),
    [results, recommended, issueTypes]
  );

  return (
    <Modal wide preventClose={isSubmitting}>
      <DialogTitle>
        {dryRunOnly ? 'Policy Engine v2 Dry Run' : 'Switch to Policy Engine v2'}
      </DialogTitle>
      <DialogContent>
        {!results && <Loader />}
        {results && (
          <Stack gap={2}>
            <Box>
              <Text bold inline>
                Organization:
              </Text>{' '}
              {orgName}
            </Box>
            {mergeStrategyLabel && (
              <Box>
                <Text bold inline>
                  Merge Strategy:
                </Text>{' '}
                {mergeStrategyLabel}
                <Text size="small" color="grey">
                  Decides which setting wins when policies with the same priority disagree. The
                  conversion always uses this strategy, because only this strategy gives each user
                  the same result as today.
                </Text>
              </Box>
            )}
            {error && <InlineToast level="error" message={error} />}
            {dryRunOnly && <InlineToast message="This is a dry run. Nothing was saved." />}
            <RouteComparison results={results} recommended={recommended} />
            <ToggleTabs
              ref={tabRef}
              panels={panels}
              onChange={(path) => setRoute(path as PolicyConversionRoute)}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          label={dryRunOnly ? 'Close' : 'Cancel'}
          type="cancel"
          onClick={() => closeModal()}
          disabled={isSubmitting}
        />
        {!dryRunOnly && (
          <Button
            label={`Convert ${RouteLabels[route]} and Switch`}
            onClick={() => handleSubmit()}
            disabled={!canSwitch}
            loading={isSubmitting}
          />
        )}
      </DialogActions>
    </Modal>
  );
};

export default PolicyConversionModal;
